import DownloadIcon from '@mui/icons-material/Download';
import MailIcon from '@mui/icons-material/Mail';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import {
  ICandidate,
  InformationReqResponse,
  getPresignedURLForGet,
  WorkspaceLogoDTO,
  getDefaultWorkspaceLogo,
  inviteShareCandidateUsersByInformation,
  getJobById,
  cancelRTRRequest,
} from 'wisestep-api';
import {
  WsColors,
  WsTypography,
  WsTypoColors,
  WsButton,
  WsIconText,
  WsIconCalendarOutline,
  formateDateInDateMonthYear,
  IConversationState,
  WsDrawer,
  IUserDTO,
  formattedDateISO,
  Button,
  WsDialog,
} from 'wisestep-base';

import { useDefaultSmtpEmail } from '../../../../components/SurveyBuilder/SurveyCommonMethods';
import { useStatusActionPipelineContext, useUserContext } from '../../../../context';
import SendRtrComponent from '../../../../features/conversationWidget/components/SendRtrComponent';
import { saveMapping } from '../../../../features/conversationWidget/utils/saveMapping';
import ExpireLogo from '../../../../images/expire.svg';
import PendingResponseLogo from '../../../../images/PendingResponse.svg';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { failed, success, warning } from '../../.././../utils/NotificationMessages';
import ConfirmationDialog from '../../../candidates/components/confirmationDialog/ConfirmationDialog';
import { getFile } from '../../../careerPageSettings/util';
import { StatusIconTypography } from '../../../users/components/TotalInvitesList';
import { interViewerPayloadHelper } from '../childComponets/CandidateEvaluation/CandidateEvaluation';

import InformationDrawer from './InformationDrawer';

const objectRTRRequested = {
  downloadSource: 'NOT_SPECIFIED',
  recruiterActivity: {
    actions: ['RTR Requested'],
    results: [],
  },
};

const objectRTRCancelled = {
  downloadSource: 'NOT_SPECIFIED',
  recruiterActivity: {
    actions: ['RTR Cancelled'],
    results: [],
  },
};

interface IRTRRequestStatus {
  initialRTRData: InformationReqResponse;
  candidateInteractionData?: ICandidate;
  isMapping: boolean;
  selectedJob: string | '';
}

const RTRRequestMappingStatus = ({
  initialRTRData,
  candidateInteractionData,
  isMapping,
  selectedJob,
}: IRTRRequestStatus) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const statusActionPipeline = useStatusActionPipelineContext();

  const [RTRData, setRTRData] = useState(initialRTRData);
  const [showRTR, setShowRTR] = useState<boolean>(false);
  const [showInformationDrawer, setShowInformationDrawer] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { defaultSmtpEmailValue, preferredProviderId } = useDefaultSmtpEmail();

  const [fileUrl, setFileUrl] = useState<string>('');
  const [showCancelConfirmation, setShowCancelConfirmation] = useState<boolean>(false);
  const conversationStateValue: IConversationState = useMemo(() => {
    return {
      candidate: candidateInteractionData,
      currentUser: loggedInUser,
      candidateLocation: null,
      candidatePreferredLocations: [],
      recentJobMappings: [],
      autoSaveDisabledSettings: null,
      autoSaveAllowed: true,
      getDailyConversationCount: null,
      totalConversationPermanentCount: null,
      messageAndInteractionPipeline: statusActionPipeline,
    };
  }, [candidateInteractionData, loggedInUser, statusActionPipeline]);

  useEffect(() => {
    const fetchLogoUrl = async () => {
      const logoResponse = await getDefaultWorkspaceLogo('LOGO');
      if (logoResponse.data) {
        const logoDetails: WorkspaceLogoDTO = logoResponse.data;
        const fileUrl = await getFile(logoDetails.s3Url.split('?')[0]);
        setFileUrl(fileUrl);
      }
    };
    fetchLogoUrl();
  }, []);

  const removeQueryParams = (url: string) => {
    const urlObj = new URL(url);
    urlObj.search = '';
    return urlObj.toString();
  };

  const RTR_STATUS = {
    RTR_ACCEPTED: 'RTR Accepted',
    RTR_EXPIRED: 'RTR Expired',
    RTR_REQUESTED: 'RTR Requested',
    RTR_REJECTED: 'RTR Rejected',
    RTR_CANCELLED: 'RTR Cancelled',
  };
  const fullUrl = `${window.location.href}&corphiringTemplate=informationrequestTemplate`;

  const callSendReminder = async () => {
    const payload = { ...interViewerPayloadHelper };
    payload.evalTemplateId = RTRData?.informationReqTemplate?.id;
    payload.interactionId = RTRData?.interactionId;
    payload.jobId = (RTRData as any)?.jobId ?? '';
    payload.reshareEmailId = (RTRData as any)?.recruiterEmail ?? '';
    try {
      await inviteShareCandidateUsersByInformation({
        subject: `Information Required for ${candidateInteractionData?.mappedJobTitle ?? ''}`,
        body: RTRData?.rtrDescription?.rtrDescriptionValue ?? '',
        interactionId: candidateInteractionData?.candidateId ?? '',
        jobId: candidateInteractionData?.jobId ?? '',
        sharedCandidateName: candidateInteractionData?.name ?? '',
        sharedJobTitle: candidateInteractionData?.mappedJobTitle ?? '',
        sharedJobLocation: candidateInteractionData?.mappedJobLocation ?? '',
        sharedCandidateEmail:
          RTRData?.rtrDescription?.candidateEmail ?? candidateInteractionData?.email,
        sharedJobCode: candidateInteractionData?.jobCode ?? '',
        informationTemplateId: String(RTRData?.id ?? ''),
        isRTR: true,
        recruiterEmail: defaultSmtpEmailValue ?? '',
        logoUrl: fileUrl ?? '',
        isMapping: false,
        otherInfo: [{ candidateProfileLink: fullUrl }],
        preferredProviderId: preferredProviderId,
      });
      if (candidateInteractionData) {
        const res = await getJobById(selectedJob);
        candidateInteractionData.createdOn = formattedDateISO(new Date());
        const response = await saveMapping(objectRTRRequested, candidateInteractionData, res?.data);
        if (response && response.status == 'success') {
          setNotification(success(`Reminder has been shared successfully !!`));
        } else {
          setNotification(failed(`Send Reminder failed !!`));
        }
      }
    } catch (error) {
      setNotification(warning(`Oops , error`));
      console.error('error', error);
    }
  };

  const cancelRTR = async () => {
    setShowCancelConfirmation(false);
    if (RTRData?.id) {
      const response = await cancelRTRRequest(RTRData?.id);
      if (response && response.data) {
        if (candidateInteractionData) {
          const res = await getJobById(selectedJob);
          await saveMapping(objectRTRCancelled, candidateInteractionData, res?.data);
        }
        setRTRData(response.data);
        setNotification(success(`RTR has been cancelled successfully !!`));
      } else {
        setNotification(failed(`Cancel RTR failed !!`));
      }
    }
  };

  const downlodPdf = async () => {
    if (RTRData) {
      const fileName = 'RTR_Response.pdf';
      const data = await getPresignedURLForGet({
        fileName: RTRData.responsePdfUrl ? removeQueryParams(RTRData.responsePdfUrl) : '',
      });
      if (data && data.data.preSignedUrl) {
        const response = await fetch(data.data.preSignedUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = candidateInteractionData?.name
          ? candidateInteractionData?.name + '_' + fileName
          : fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    }
  };

  const showRTRHandler = () => {
    setShowRTR(!showRTR);
  };

  const isNotEmptyObject = (obj: { [key: string]: string | number | object }): boolean =>
    !!obj && typeof obj === 'object' && !Array.isArray(obj) && Object.keys(obj).length > 0;

  return (
    <>
      {RTRData && (
        <Grid
          container
          mt={2}
          px={1}
          sx={{
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: WsColors.White,
            borderRadius: 2,
            border: '1px solid',
            borderColor: WsColors.TertiaryShades500,
          }}
        >
          <Grid
            container
            xs={3}
            p={1}
            sx={{
              backgroundColor: WsColors.White,
              cursor: 'pointer',
            }}
          >
            <Grid item xs={9} display="flex" alignItems="center" alignContent="center">
              <WsTypography color={WsTypoColors.PRIMARY} bold cursor="pointer">
                Right To Represent
              </WsTypography>
            </Grid>
          </Grid>
          <Grid item container direction="row" alignItems="center" justifyContent="flex-end" xs={9}>
            {RTRData.requestedOn ? (
              <>
                <WsIconText
                  icon={<WsIconCalendarOutline />}
                  text={formateDateInDateMonthYear(RTRData.requestedOn ?? '')}
                />
                {RTRData.rtrFeedback === RTR_STATUS.RTR_ACCEPTED ? (
                  <StatusIconTypography cell="Accepted" />
                ) : RTRData.rtrFeedback === RTR_STATUS.RTR_REJECTED ? (
                  <StatusIconTypography cell="Declined" />
                ) : RTRData.rtrFeedback === RTR_STATUS.RTR_CANCELLED ? (
                  <StatusIconTypography cell="Cancelled" />
                ) : RTRData.rtrFeedback === RTR_STATUS.RTR_REQUESTED ? (
                  <>
                    <img
                      src={PendingResponseLogo}
                      alt="Pending Response Logo"
                      style={{
                        width: 200,
                        height: 30,
                      }}
                    />
                    <Grid item display="flex" flexDirection="row" alignItems="center">
                      <IconButton>
                        {RTRData.emailStatusCode ? (
                          RTRData.emailStatusCode === 'SUCCESS' ? (
                            <Tooltip title={RTRData.emailStatusCode}>
                              <MarkEmailReadIcon fontSize="large" color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={RTRData.emailStatusCode}>
                              <UnsubscribeIcon fontSize="large" color="error" />
                            </Tooltip>
                          )
                        ) : (
                          <Tooltip title="QUEUED">
                            <MailIcon fontSize="large" color="warning" />
                          </Tooltip>
                        )}
                      </IconButton>
                    </Grid>
                    <Grid
                      item
                      spacing={1}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Grid item mr={0.5}>
                        <Button
                          type="button"
                          onClick={() => setShowCancelConfirmation(true)}
                          variant="outlined"
                          size="small"
                        >
                          Cancel RTR
                        </Button>
                      </Grid>
                      <Grid item>
                        <WsButton onClick={callSendReminder} variant="contained" size="small">
                          Send Reminder
                        </WsButton>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <img
                    src={ExpireLogo}
                    alt="Expired Logo"
                    style={{
                      width: 100,
                      height: 30,
                      marginRight: 8,
                    }}
                  />
                )}

                {(RTRData.rtrFeedback === RTR_STATUS.RTR_ACCEPTED ||
                  RTRData.rtrFeedback === RTR_STATUS.RTR_REJECTED) && (
                  <Grid ml={1}>
                    <WsButton onClick={downlodPdf} startIcon={<DownloadIcon />}>
                      Download
                    </WsButton>
                  </Grid>
                )}
                {RTRData.rtrFeedback === RTR_STATUS.RTR_ACCEPTED &&
                  isNotEmptyObject(RTRData.response) && (
                    <Grid ml={1}>
                      <IconButton onClick={() => setShowInformationDrawer(true)}>
                        <Tooltip title={'Show Information'}>
                          <VisibilityIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                        </Tooltip>
                      </IconButton>
                    </Grid>
                  )}
                {(RTRData.rtrFeedback === RTR_STATUS.RTR_CANCELLED ||
                  RTRData.rtrFeedback === RTR_STATUS.RTR_EXPIRED) && (
                  <Grid ml={1}>
                    <WsButton onClick={showRTRHandler} variant="contained">
                      Request RTR
                    </WsButton>
                  </Grid>
                )}
              </>
            ) : (
              <WsButton onClick={showRTRHandler} variant="contained">
                Request RTR
              </WsButton>
            )}
          </Grid>
        </Grid>
      )}
      {showRTR && (
        <WsDrawer open={showRTR} size={'medium'} onClose={showRTRHandler} title="Request RTR">
          <SendRtrComponent
            state={conversationStateValue}
            closeWidgetDrawer={showRTRHandler}
            isUnifiedView={true}
            minHeight="calc(100vh - 57px)"
            isMapping={isMapping}
            onRtrRequested={() => {
              setRTRData({
                ...RTRData,
                rtrFeedback: RTR_STATUS.RTR_REQUESTED,
                sendRequest: true,
                requestedOn: formattedDateISO(new Date()),
              });
            }}
          />
        </WsDrawer>
      )}
      {showInformationDrawer && (
        <InformationDrawer
          open={showInformationDrawer}
          onClose={() => setShowInformationDrawer(false)}
          RTRData={RTRData}
        />
      )}
      {showCancelConfirmation && (
        <WsDialog
          openDialog={showCancelConfirmation}
          onClose={() => setShowCancelConfirmation(false)}
        >
          <ConfirmationDialog
            text="Are you sure you want to cancel RTR?"
            title="Cancel RTR"
            confirm={cancelRTR}
            close={() => setShowCancelConfirmation(false)}
          />
        </WsDialog>
      )}
    </>
  );
};

export default RTRRequestMappingStatus;
